import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { reorderFlatItems, ChildItem, OrderItem } from '../AdjustTableDialog.utils';
import { FlatItem } from '../components/SortableItem';

type UseAdjustTableDragParams = {
  flatPinnedItems: FlatItem[];
  flatUnpinnedItems: FlatItem[];
  itemMap: Map<string, OrderItem>;
  setPinnedOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setUnpinnedOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setChildOrderMap: React.Dispatch<React.SetStateAction<Map<string, ChildItem[]>>>;
};

export function useAdjustTableDrag({
  flatPinnedItems,
  flatUnpinnedItems,
  itemMap,
  setPinnedOrder,
  setUnpinnedOrder,
  setChildOrderMap,
}: UseAdjustTableDragParams): {
  sensors: ReturnType<typeof useSensors>;
  draggingSection: 'pinned' | 'unpinned' | null;
  draggedItemId: string | null;
  isDropDisallowed: boolean;
  flatPinnedIds: string[];
  flatUnpinnedIds: string[];
  handlePinnedDragStart: (event: DragStartEvent) => void;
  handlePinnedDragEnd: (event: DragEndEvent) => void;
  handlePinnedDragOver: (event: DragOverEvent) => void;
  handleUnpinnedDragStart: (event: DragStartEvent) => void;
  handleUnpinnedDragEnd: (event: DragEndEvent) => void;
  handleUnpinnedDragOver: (event: DragOverEvent) => void;
  handleDragReset: () => void;
} {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const [draggingSection, setDraggingSection] = useState<'pinned' | 'unpinned' | null>(null);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [isDropDisallowed, setIsDropDisallowed] = useState(false);

  const flatPinnedIds = useMemo(() => flatPinnedItems.map(flatItem => flatItem.id), [flatPinnedItems]);
  const flatUnpinnedIds = useMemo(() => flatUnpinnedItems.map(flatItem => flatItem.id), [flatUnpinnedItems]);

  const checkIsDropValid = useCallback((flatItems: FlatItem[], activeId: string, overId: string | null): boolean => {
    if (!overId || activeId === overId) return true;
    const activeItem = flatItems.find(flatItem => flatItem.id === activeId);
    const overItem = flatItems.find(flatItem => flatItem.id === overId);
    if (!activeItem || !overItem) return false;
    return activeItem.parentId === overItem.parentId;
  }, []);

  const handleDragReset = useCallback(() => {
    setDraggingSection(null);
    setDraggedItemId(null);
    setIsDropDisallowed(false);
  }, []);

  const handleSectionDragEnd = useCallback(
    (flatItems: FlatItem[], setOrder: React.Dispatch<React.SetStateAction<string[]>>) => (event: DragEndEvent) => {
      setDraggingSection(null);
      setDraggedItemId(null);
      setIsDropDisallowed(false);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const result = reorderFlatItems(flatItems, String(active.id), String(over.id));
      if (!result) return;
      setOrder(result.newParentOrder);
      if (result.newChildOrderMap.size > 0) {
        setChildOrderMap(previousMap => {
          const nextMap = new Map(previousMap);
          result.newChildOrderMap.forEach((childColumnIds, parentId) => {
            const currentChildren = previousMap.get(parentId) ?? itemMap.get(parentId)?.children ?? [];
            const childLookup = new Map(currentChildren.map(child => [child.columnId, child]));
            const reordered = childColumnIds
              .map(childColumnId => childLookup.get(childColumnId))
              .filter((child): child is ChildItem => child !== undefined);
            nextMap.set(parentId, reordered);
          });
          return nextMap;
        });
      }
    },
    [itemMap, setChildOrderMap],
  );

  const handleSectionDragOver = useCallback(
    (flatItems: FlatItem[]) => (event: DragOverEvent) => {
      const { active, over } = event;
      const isValid = checkIsDropValid(flatItems, String(active.id), over ? String(over.id) : null);
      setIsDropDisallowed(!isValid);
    },
    [checkIsDropValid],
  );

  const handlePinnedDragStart = useCallback((event: DragStartEvent) => {
    setDraggingSection('pinned');
    setDraggedItemId(String(event.active.id));
    setIsDropDisallowed(false);
  }, []);

  const handlePinnedDragEnd = useMemo(
    () => handleSectionDragEnd(flatPinnedItems, setPinnedOrder),
    [handleSectionDragEnd, flatPinnedItems, setPinnedOrder],
  );

  const handlePinnedDragOver = useMemo(
    () => handleSectionDragOver(flatPinnedItems),
    [handleSectionDragOver, flatPinnedItems],
  );

  const handleUnpinnedDragStart = useCallback((event: DragStartEvent) => {
    setDraggingSection('unpinned');
    setDraggedItemId(String(event.active.id));
    setIsDropDisallowed(false);
  }, []);

  const handleUnpinnedDragEnd = useMemo(
    () => handleSectionDragEnd(flatUnpinnedItems, setUnpinnedOrder),
    [handleSectionDragEnd, flatUnpinnedItems, setUnpinnedOrder],
  );

  const handleUnpinnedDragOver = useMemo(
    () => handleSectionDragOver(flatUnpinnedItems),
    [handleSectionDragOver, flatUnpinnedItems],
  );

  useEffect(() => {
    if (!isDropDisallowed) return;
    const previousCursor = document.documentElement.style.cursor;
    document.documentElement.style.cursor = 'not-allowed';
    return () => {
      document.documentElement.style.cursor = previousCursor;
    };
  }, [isDropDisallowed]);

  return {
    sensors,
    draggingSection,
    draggedItemId,
    isDropDisallowed,
    flatPinnedIds,
    flatUnpinnedIds,
    handlePinnedDragStart,
    handlePinnedDragEnd,
    handlePinnedDragOver,
    handleUnpinnedDragStart,
    handleUnpinnedDragEnd,
    handleUnpinnedDragOver,
    handleDragReset,
  };
}
